import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  CardTitle,
  Row,
  Col,
  Table,
  Form,
  FormGroup,
  Input,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";

import { API_BASE_URL } from "../config/api";
import { ItemOrderControls, ItemOrderMessage, useItemOrdering } from "../components/ItemOrderControls";

const BACKEND_BASE_URL = API_BASE_URL;

const emptyItem = {
  title: "",
  title_ar: "",
  price: "",
  content: "",
  content_ar: "",
};

const emptyUpdateItem = {
  title: "",
  title_ar: "",
  price: "",
  content: "",
  content_ar: "",
  image_path: "",
  image_paths: [],
};

const buildImageUrl = (path) => {
  if (!path) {
    return "";
  }

  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  return `${BACKEND_BASE_URL}/${`${path}`.replace(/^\/+/, "")}`;
};

const extractImagePaths = (item) => {
  if (Array.isArray(item?.image_paths) && item.image_paths.length) {
    return item.image_paths.filter(Boolean);
  }

  if (typeof item?.image_paths === "string" && item.image_paths.trim()) {
    try {
      const parsed = JSON.parse(item.image_paths);
      if (Array.isArray(parsed)) {
        return parsed.filter(Boolean);
      }
    } catch (err) {
      return item.image_paths
        .split(",")
        .map((path) => path.trim())
        .filter(Boolean);
    }
  }

  if (item?.image_path) {
    return [item.image_path];
  }

  return [];
};

const normalizePriceInput = (price) => `${price || ""}`.replace(/\s*JD$/i, "").trim();

const moveArrayItem = (items, index, direction) => {
  const targetIndex = index + direction;
  if (index < 0 || targetIndex < 0 || index >= items.length || targetIndex >= items.length) {
    return items;
  }
  const nextItems = [...items];
  [nextItems[index], nextItems[targetIndex]] = [nextItems[targetIndex], nextItems[index]];
  return nextItems;
};

const ShopImageOrderEditor = ({ title, images = [], onMove }) => {
  if (!images.length) return null;

  return (
    <div style={{ marginTop: "12px" }}>
      <strong>{title}</strong>
      {images.map((image, index) => {
        const isFile = image instanceof File;
        const path = isFile ? "" : image;
        return (
          <div
            key={isFile ? `${image.name}-${image.lastModified}-${index}` : `${path}-${index}`}
            style={{ display: "flex", alignItems: "center", gap: "12px", padding: "10px", marginTop: "8px", border: "1px solid #ddd", borderRadius: "6px", background: "#fff" }}
          >
            <strong style={{ minWidth: "28px" }}>{index + 1}</strong>
            <div style={{ flex: 1 }}>
              {isFile ? image.name : (
                <img src={buildImageUrl(path)} alt={`Order ${index + 1}`} style={{ width: "90px", height: "70px", objectFit: "cover", borderRadius: "8px" }} />
              )}
              {index === 0 && <div style={{ color: "#7a651f", fontWeight: "600" }}>Main image</div>}
            </div>
            <div style={{ whiteSpace: "nowrap" }}>
              <Button type="button" size="sm" color="secondary" disabled={index === 0} onClick={() => onMove(index, -1)}>↑</Button>{" "}
              <Button type="button" size="sm" color="secondary" disabled={index === images.length - 1} onClick={() => onMove(index, 1)}>↓</Button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

function TankShop() {
  const [items, setItems] = useState([]);
  const [newItem, setNewItem] = useState(emptyItem);
  const [itemImages, setItemImages] = useState([]);
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [updateItem, setUpdateItem] = useState(emptyUpdateItem);
  const [updateImages, setUpdateImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [itemCount, setItemCount] = useState(0);
  const [addError, setAddError] = useState("");
  const [addImageError, setAddImageError] = useState("");
  const [updateError, setUpdateError] = useState("");
  const [addSuccessMessage, setAddSuccessMessage] = useState("");
  const [updateSuccessMessage, setUpdateSuccessMessage] = useState("");
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const { moveItem, orderMessage, savingOrder } = useItemOrdering(items, setItems, `${BACKEND_BASE_URL}/shop/order`);

  const isValidImageExtension = (fileName) => {
    const validExtensions = [".webp", ".gif", ".png", ".jpg", ".jpeg"];
    const fileExtension = fileName
      .slice(((fileName.lastIndexOf(".") - 1) >>> 0) + 2)
      .toLowerCase();

    return validExtensions.includes(`.${fileExtension}`);
  };

  const resetAddFileInput = () => {
    const input = document.getElementById("new-item-images");
    if (input) {
      input.value = "";
    }
  };

  const resetUpdateFileInput = () => {
    const input = document.getElementById("update-item-images");
    if (input) {
      input.value = "";
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewItem((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    setItemImages(Array.from(e.target.files || []));
  };

  const moveNewImage = (index, direction) => {
    setItemImages((images) => moveArrayItem(images, index, direction));
  };

  const handleUpdateImageChange = (e) => {
    setUpdateImages(Array.from(e.target.files || []));
  };

  const moveUpdateImage = (index, direction) => {
    if (updateImages.length > 0) {
      setUpdateImages((images) => moveArrayItem(images, index, direction));
      return;
    }

    setUpdateItem((item) => ({
      ...item,
      image_paths: moveArrayItem(item.image_paths || [], index, direction),
    }));
  };

  const resetUpdateForm = () => {
    setUpdateItem(emptyUpdateItem);
    setUpdateImages([]);
    setSelectedItemId(null);
    setShowUpdateForm(false);
    resetUpdateFileInput();
  };

  const fetchData = async () => {
    try {
      const response = await axios.get(`${BACKEND_BASE_URL}/shop/list`);
      const nextItems = (response.data.data || []).map((item) => {
        const imagePaths = extractImagePaths(item);
        return {
          ...item,
          image_path: imagePaths[0] || item.image_path || "",
          image_paths: imagePaths,
        };
      });

      setItems(nextItems);
      setItemCount(nextItems.length);
      setLoading(false);
      setError(null);
    } catch (err) {
      console.error(`Error getting data: ${err}`);
      setLoading(false);
      setError("Error fetching data. Please try again later.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !newItem.title.trim() ||
      !newItem.title_ar.trim() ||
      !newItem.price.trim() ||
      !newItem.content.trim() ||
      !newItem.content_ar.trim() ||
      itemImages.length === 0
    ) {
      setAddError("All fields are required.");
      setTimeout(() => {
        setAddError("");
      }, 5000);
      return;
    }

    const invalidImage = itemImages.find((image) => !isValidImageExtension(image.name));
    if (invalidImage) {
      setAddImageError("Invalid file extension. Please upload .WEBP, .GIF, .PNG, .JPG, or .JPEG");
      setTimeout(() => {
        setAddImageError("");
      }, 5000);
      return;
    }

    setAddImageError("");

    try {
      const formData = new FormData();
      itemImages.forEach((image) => {
        formData.append("images", image);
      });
      formData.append("title", newItem.title);
      formData.append("title_ar", newItem.title_ar);
      formData.append("price", newItem.price);
      formData.append("content", newItem.content);
      formData.append("content_ar", newItem.content_ar);

      await axios.post(`${BACKEND_BASE_URL}/shop/add`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      fetchData();
      setNewItem(emptyItem);
      setItemImages([]);
      resetAddFileInput();
      setAddSuccessMessage("Tank Shop added successfully");
      setTimeout(() => {
        setAddSuccessMessage("");
      }, 5000);
    } catch (err) {
      console.error("Error adding item:", err);
      setAddError("Error occurred while adding the item.");
    }
  };

  const handleDelete = (itemId) => {
    setDeleteItemId(itemId);
    setShowDeletePopup(true);
  };

  const performDelete = async () => {
    try {
      await axios.delete(`${BACKEND_BASE_URL}/shop/delete/${deleteItemId}`);
      setItems((prev) => {
        const nextItems = prev.filter((item) => item.id !== deleteItemId);
        setItemCount(nextItems.length);
        return nextItems;
      });
      setShowDeletePopup(false);
    } catch (err) {
      console.error("Error deleting item:", err);
    }
  };

  const handleUpdate = (item) => {
    const imagePaths = extractImagePaths(item);

    setSelectedItemId(item.id);
    setUpdateItem({
      title: item.title,
      title_ar: item.title_ar,
      price: normalizePriceInput(item.price),
      content: item.content,
      content_ar: item.content_ar,
      image_path: imagePaths[0] || "",
      image_paths: imagePaths,
    });
    setUpdateImages([]);
    setShowUpdateForm(true);
    resetUpdateFileInput();
  };

  const handleItemUpdate = async (e) => {
    e.preventDefault();

    if (
      !updateItem.title.trim() ||
      !updateItem.title_ar.trim() ||
      !updateItem.price.trim() ||
      !updateItem.content.trim() ||
      !updateItem.content_ar.trim()
    ) {
      setUpdateError("Title, Price, and Content are required.");
      setTimeout(() => setUpdateError(""), 5000);
      return;
    }

    const invalidImage = updateImages.find((image) => !isValidImageExtension(image.name));
    if (invalidImage) {
      setUpdateError("Invalid file extension. Allowed: .WEBP, .GIF, .PNG, .JPG, .JPEG");
      setTimeout(() => setUpdateError(""), 5000);
      return;
    }

    const formData = new FormData();
    formData.append("title", updateItem.title);
    formData.append("title_ar", updateItem.title_ar);
    formData.append("price", updateItem.price);
    formData.append("content", updateItem.content);
    formData.append("content_ar", updateItem.content_ar);

    if (updateImages.length > 0) {
      updateImages.forEach((image) => {
        formData.append("images", image);
      });
    } else {
      formData.append("currentImages", JSON.stringify(updateItem.image_paths || []));
    }

    try {
      await axios.put(`${BACKEND_BASE_URL}/shop/update/${selectedItemId}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      fetchData();
      setUpdateSuccessMessage("Tank Shop updated successfully");
      setTimeout(() => {
        setUpdateSuccessMessage("");
      }, 5000);
      resetUpdateForm();
    } catch (err) {
      console.error("Error updating item:", err);
      setUpdateError("Error occurred while updating the item.");
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (showUpdateForm) {
      const updateTitleInputField = document.getElementById("update-item-title");
      if (updateTitleInputField) {
        updateTitleInputField.focus();
      }
    }
  }, [showUpdateForm]);

  return (
    <>
      <div className="content">
        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md="6">
              <FormGroup>
                <label>Title:</label>
                <Input type="text" name="title" value={newItem.title} onChange={handleInputChange} />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <label>Arabic Title:</label>
                <Input type="text" name="title_ar" value={newItem.title_ar} onChange={handleInputChange} />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <label>Price:</label>
                <Input type="number" name="price" value={newItem.price} onChange={handleInputChange} />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md="7">
              <FormGroup>
                <label htmlFor="content" className="textarea-label">Content:</label>
                <textarea
                  className="form-control"
                  id="content"
                  name="content"
                  value={newItem.content}
                  onChange={handleInputChange}
                  style={{ minHeight: "300px" }}
                />
              </FormGroup>
            </Col>
            <Col md="7">
              <FormGroup>
                <label htmlFor="content_ar" className="textarea-label">Arabic Content:</label>
                <textarea
                  className="form-control"
                  id="content_ar"
                  name="content_ar"
                  value={newItem.content_ar}
                  onChange={handleInputChange}
                  style={{ minHeight: "300px" }}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md="12">
              <FormGroup>
                <label>Images:</label>
                <Input
                  id="new-item-images"
                  type="file"
                  name="images"
                  multiple
                  onChange={handleImageChange}
                />
                {itemImages.length > 0 && (
                  <ShopImageOrderEditor
                    title="Images Order (image 1 is the main image)"
                    images={itemImages}
                    onMove={moveNewImage}
                  />
                )}
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md="12">
              <div className="update ml-auto mr-auto">
                <Button className="btn-round" color="primary" type="submit">
                  Add Item
                </Button>
                {addSuccessMessage && (
                  <div className="alert alert-success" role="alert" style={{ width: "50%", margin: "10px auto", textAlign: "center" }}>
                    {addSuccessMessage}
                  </div>
                )}
                {addError && (
                  <div className="alert alert-danger" role="alert" style={{ width: "50%", margin: "10px auto", textAlign: "center" }}>
                    {addError}
                  </div>
                )}
                {addImageError && <div style={{ color: "red" }}>{addImageError}</div>}
              </div>
            </Col>
          </Row>
        </Form>

        <div className="content">
          <CardFooter>
            <div>Total Items: {itemCount}</div>
          </CardFooter>
        </div>

        <div className="content">
          <Row>
            <Col md="12">
              <Card>
                <CardHeader>
                  <CardTitle tag="h4">Shop Items List</CardTitle>
                </CardHeader>
                <CardBody>
                  <ItemOrderMessage message={orderMessage} />
                  {loading ? (
                    <div>Loading...</div>
                  ) : error ? (
                    <div className="alert alert-danger" role="alert">{error}</div>
                  ) : (
                    <Table responsive>
                      <thead className="text-primary">
                        <tr>
                          <th>Title</th>
                          <th>Arabic Title</th>
                          <th>Price</th>
                          <th>Content</th>
                          <th>Arabic Content</th>
                          <th>Images</th>
                          <th>Order</th>
                          <th>Update</th>
                          <th>Delete</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item, index) => (
                          <tr key={item.id}>
                            <td>{item.title}</td>
                            <td>{item.title_ar}</td>
                            <td>{item.price}</td>
                            <td>{item.content}</td>
                            <td>{item.content_ar}</td>
                            <td>
                              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                                {item.image_paths.map((path, index) => (
                                  <img
                                    key={`${item.id}-image-${index}`}
                                    src={buildImageUrl(path)}
                                    alt={`${item.title}-${index + 1}`}
                                    style={{ width: "70px", height: "70px", objectFit: "cover", borderRadius: "8px" }}
                                  />
                                ))}
                              </div>
                            </td>
                            <td><ItemOrderControls index={index} count={items.length} onMove={moveItem} disabled={savingOrder} /></td>
                            <td>
                              <Button color="success" onClick={() => handleUpdate(item)}>
                                Update
                              </Button>
                            </td>
                            <td>
                              <Button color="danger" onClick={() => handleDelete(item.id)}>
                                Delete
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      {showUpdateForm && (
        <div className="content">
          <Form onSubmit={handleItemUpdate}>
            <Row>
              <Col md="6">
                <FormGroup>
                  <label>Title:</label>
                  <Input
                    id="update-item-title"
                    type="text"
                    name="title"
                    value={updateItem.title}
                    onChange={(e) => setUpdateItem((prev) => ({ ...prev, title: e.target.value }))}
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <label>Arabic Title:</label>
                  <Input
                    type="text"
                    name="title_ar"
                    value={updateItem.title_ar}
                    onChange={(e) => setUpdateItem((prev) => ({ ...prev, title_ar: e.target.value }))}
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <label>Price:</label>
                  <Input
                    type="number"
                    name="price"
                    value={updateItem.price}
                    onChange={(e) => setUpdateItem((prev) => ({ ...prev, price: e.target.value }))}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md="12">
                <FormGroup>
                  <label htmlFor="update-content" className="textarea-label">Content:</label>
                  <textarea
                    id="update-content"
                    className="form-control"
                    style={{ minHeight: "300px", width: "590px" }}
                    name="content"
                    value={updateItem.content}
                    onChange={(e) => setUpdateItem((prev) => ({ ...prev, content: e.target.value }))}
                  />
                </FormGroup>
              </Col>
              <Col md="12">
                <FormGroup>
                  <label htmlFor="update-content-ar" className="textarea-label">Arabic Content:</label>
                  <textarea
                    id="update-content-ar"
                    className="form-control"
                    style={{ minHeight: "300px", width: "590px" }}
                    name="content_ar"
                    value={updateItem.content_ar}
                    onChange={(e) => setUpdateItem((prev) => ({ ...prev, content_ar: e.target.value }))}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md="12">
                <FormGroup>
                  <label>Current Images:</label>
                  <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "12px" }}>
                    {updateItem.image_paths.map((path, index) => (
                      <img
                        key={`current-image-${index}`}
                        src={buildImageUrl(path)}
                        alt={`Item ${index + 1}`}
                        style={{ width: "110px", height: "110px", objectFit: "cover", borderRadius: "10px" }}
                      />
                    ))}
                  </div>
                  <label>Update Images (optional, replaces all current images):</label>
                  <Input
                    id="update-item-images"
                    type="file"
                    name="images"
                    multiple
                    onChange={handleUpdateImageChange}
                  />
                  {updateImages.length > 0 && (
                    <ShopImageOrderEditor
                      title="Replacement Images Order (image 1 is the main image)"
                      images={updateImages}
                      onMove={moveUpdateImage}
                    />
                  )}
                  {updateImages.length === 0 && (
                    <ShopImageOrderEditor
                      title="Current Images Order (image 1 is the main image)"
                      images={updateItem.image_paths}
                      onMove={moveUpdateImage}
                    />
                  )}
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md="12">
                <div className="update ml-auto mr-auto">
                  <Button className="btn-round" color="primary" type="submit">
                    Update Item
                  </Button>{" "}
                  <Button className="btn-round" color="secondary" type="button" onClick={resetUpdateForm}>
                    Cancel
                  </Button>
                  {updateSuccessMessage && (
                    <div className="alert alert-success" role="alert" style={{ width: "50%", margin: "10px auto", textAlign: "center" }}>
                      {updateSuccessMessage}
                    </div>
                  )}
                  {updateError && (
                    <div className="alert alert-danger" role="alert" style={{ width: "50%", margin: "10px auto", textAlign: "center" }}>
                      {updateError}
                    </div>
                  )}
                </div>
              </Col>
            </Row>
          </Form>
        </div>
      )}

      <Modal isOpen={showDeletePopup} toggle={() => setShowDeletePopup(false)}>
        <ModalHeader toggle={() => setShowDeletePopup(false)}>Confirm Delete</ModalHeader>
        <ModalBody>Are you sure you want to delete this item?</ModalBody>
        <ModalFooter>
          <Button color="danger" onClick={performDelete}>Delete</Button>
          <Button color="secondary" onClick={() => setShowDeletePopup(false)}>Cancel</Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default TankShop;
